import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DateUtil } from '@uzurv/core';
import { UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ApprovalRequestSubmission,
  IgnoredRideRequest,
  InterestedDriver,
  Provider,
  ProviderDetail,
  ProviderDispatcher,
  ProviderDriver,
  ProviderFeeSchedule,
  ProviderFeeScheduleApprovalObjectInfo,
  ProviderFeeScheduleApprovalStatus,
  ProviderRequest,
  ProviderServiceLevel,
  Reservation,
  ProviderDispatchFeedItem,
  TechnologyPlatform
} from '../../models';

@Injectable({
  providedIn: 'root'
})
export class ProviderService extends UzurvRestService {
  readonly resourceUrl = 'providers/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * @param pageNumber
   * @param pageSize
   * @permission provider_read
   */
  listAll(pageNumber: number = 0, pageSize: number = 200): Observable<Array<Provider>> {
    return this.get<Array<Provider>>(this.resourceUrl, { pageNumber: pageNumber, pageSize: pageSize }).pipe(
      map(response => Deserialize(response, Provider))
    );
  }

  /**
   * @param searchString
   * @param pageNumber
   * @param pageSize
   * @permission provider_read
   */
  searchProviders(searchString: string, pageNumber: number, pageSize: number): Observable<Array<Provider>> {
    // TODO: change to `resourceURL` when providerController is refactored to providerController
    return this.get<Array<Provider>>(`search/providers/`, {
      searchString: searchString,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, Provider)));
  }

  /**
   * @param id
   * @permission provider_read
   */
  findById(id: number): Observable<ProviderDetail> {
    return this.get<ProviderDetail>(`${this.resourceUrl}${id}`).pipe(map(response => Deserialize(response, ProviderDetail)));
  }

  /**
   *
   */
  listActiveTechnologyPlatforms(): Observable<Array<TechnologyPlatform>> {
    return this.get<Array<TechnologyPlatform>>(`${this.resourceUrl}technologyPlatforms/`).pipe(
      map(response => Deserialize(response, TechnologyPlatform))
    );
  }

  /**
   * @permission provider_read
   */
  listForCurrentUser(): Observable<Provider> {
    return this.get<Provider>(this.resourceUrl, {
      forCurrentUser: true
    }).pipe(map(response => Deserialize(response, Provider)));
  }

  /**
   * @permission provider_fee_schedule_read
   */
  listProviderFeeSchedulesByProviderId(id: number): Observable<Array<ProviderFeeScheduleApprovalStatus>> {
    return this.get<Array<ProviderFeeScheduleApprovalStatus>>(`${this.resourceUrl}${id}/providerFeeSchedules/`).pipe(
      map(response => Deserialize(response, ProviderFeeScheduleApprovalStatus))
    );
  }

  /**
   * @permission provider_read
   */
  listProviderServiceLevelsByProviderId(id: number): Observable<Array<ProviderServiceLevel>> {
    return this.get<Array<ProviderServiceLevel>>(`${this.resourceUrl}${id}/serviceLevels/`);
  }

  /**
   * @param pageNumber
   * @param pageSize
   * @param upperLatitude
   * @param leftLongitude
   * @param lowerlatitude
   * @param rightLongitude
   * @param beginDate
   * @param endDate
   * @param beginTime
   * @param endTime
   * @param includeIgnored
   * @permission provider_read
   */
  listProviderFeedWithinCoordinateDiagonal(
    providerId: number,
    pageNumber: number,
    pageSize: number,
    upperLatitude: number,
    leftLongitude: number,
    lowerlatitude: number,
    rightLongitude: number,
    accepted: boolean,
    includeIgnored: boolean,
    beginDate: Date,
    endDate: Date,
    beginTime: string,
    endTime: string,
    onDemand: boolean
  ): Observable<Array<ProviderDispatchFeedItem>> {
    return this.get<Array<ProviderDispatchFeedItem>>(`${this.resourceUrl}${providerId}/rideRequests/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      upperLatitude: upperLatitude,
      leftLongitude: leftLongitude,
      lowerLatitude: lowerlatitude,
      rightLongitude: rightLongitude,
      accepted: accepted,
      includeIgnored: includeIgnored,
      beginDate: DateUtil.serializeLocalDate(beginDate),
      endDate: DateUtil.serializeLocalDate(endDate),
      beginTime: beginTime,
      endTime: endTime,
      onDemand: onDemand
    }).pipe(map(response => Deserialize(response, ProviderDispatchFeedItem)));
  }

  listRideRequestsByItineraryId(providerId: number, itineraryId: number): Observable<Array<ProviderDispatchFeedItem>> {
    return this.get<Array<ProviderDispatchFeedItem>>(`${this.resourceUrl}${providerId}/itineraries/${itineraryId}/rideRequests/`).pipe(
      map(response => Deserialize(response, ProviderDispatchFeedItem))
    );
  }

  /**
   * @param id
   * @param pageNumber
   * @param pageSize
   * @permission provider_read
   */
  listAllProviderDrivers(id: number, pageNumber: number, pageSize: number): Observable<Array<ProviderDriver>> {
    return this.get<Array<ProviderDriver>>(`${this.resourceUrl}${id}/drivers/`, { pageNumber: pageNumber, pageSize: pageSize }).pipe(
      map(response => Deserialize(response, ProviderDriver))
    );
  }

  /**
   * @param id
   * @param pageNumber
   * @param pageSize
   * @permission provider_read
   */
  listAllProviderDispatchers(id: number, pageNumber: number, pageSize: number): Observable<Array<ProviderDispatcher>> {
    return this.get<Array<ProviderDispatcher>>(`${this.resourceUrl}${id}/dispatchers/`, {
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, ProviderDispatcher)));
  }

  /**
   * @param provider
   * @permission provider_management
   */
  save(providerRequest: ProviderRequest): Observable<ProviderRequest> {
    return this.post<ProviderRequest>(this.resourceUrl, Serialize(providerRequest, ProviderRequest)).pipe(
      map(response => Deserialize(response, ProviderRequest))
    );
  }

  /**
   * @param id
   * @param providerRequest
   * @permission provider_management
   */
  updateProvider(id: number, providerRequest: ProviderRequest): Observable<ProviderRequest> {
    return this.put<ProviderRequest>(`${this.resourceUrl}${id}`, Serialize(providerRequest, ProviderRequest)).pipe(
      map(response => Deserialize(response, ProviderRequest))
    );
  }

  /**
   *
   * @permission provider_management
   */
  saveProviderServiceLevel(providerServiceLevel): Observable<ProviderServiceLevel> {
    return this.post<ProviderServiceLevel>(
      `${this.resourceUrl}${providerServiceLevel.providerId}/serviceLevels`,
      providerServiceLevel
    ).pipe(map(response => Deserialize(response, ProviderServiceLevel)));
  }

  /**
   * @permission provider_fee_schedule_create
   */
  saveProviderFeeSchedule(id: number, approvalRequestSubmission: ApprovalRequestSubmission): Observable<ProviderFeeSchedule> {
    return this.post<ApprovalRequestSubmission>(
      `${this.resourceUrl}${id}/providerFeeSchedules/`,
      Serialize(approvalRequestSubmission)
    ).pipe(map(response => Deserialize(response, ProviderFeeSchedule)));
  }

  /**
   *
   * @param providerId
   * @param providerFeeScheduleId
   * @param providerFeeSchedule
   * @permission provider_fee_schedule_update
   */
  updateProviderFeeSchedule(
    providerId: number,
    providerFeeScheduleId: number,
    providerFeeSchedule: ProviderFeeSchedule
  ): Observable<ProviderFeeSchedule> {
    return this.put<ProviderFeeSchedule>(
      `${this.resourceUrl}${providerId}/providerFeeSchedules/${providerFeeScheduleId}`,
      Serialize(providerFeeSchedule, ProviderFeeSchedule)
    ).pipe(map(response => Deserialize(response, ProviderFeeSchedule)));
  }

  /**
   * Removes provider fee schedule
   *
   * @param providerId
   * @param providerFeeScheduleId
   * @permission provider_fee_schedule_read
   */
  deleteProviderFeeSchedule(providerId: number, providerFeeScheduleId: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${providerId}/providerFeeSchedule/${providerFeeScheduleId}`);
  }

  /**
   * @param providerId
   * @param providerFeeScheduleId
   * @permission provider_fee_schedule_read
   */
  findProviderFeeScheduleInfoById(providerId: number, providerFeeScheduleId: number): Observable<ProviderFeeScheduleApprovalObjectInfo> {
    return this.get(`${this.resourceUrl}${providerId}/providerFeeSchedules/${providerFeeScheduleId}`).pipe(
      map(response => Deserialize(response, ProviderFeeScheduleApprovalObjectInfo))
    );
  }

  /**
   * @param id
   * @param operator
   * @param tenantIds
   * permission user_management
   */
  saveProviderManager(id: number, operator: ProviderDispatcher, tenantIds: Array<number>): Observable<ProviderDispatcher> {
    return this.post<ProviderDispatcher>(`${this.resourceUrl}${id}/managers/`, operator, { params: { tenantIds } }).pipe(
      map(response => Deserialize(response, ProviderDispatcher))
    );
  }

  /**
   * @param id
   * @param providerDriver
   * @permission provider_create
   */
  saveProviderDriver(id: number, providerDriver: ProviderDriver): Observable<ProviderDriver> {
    return this.post<ProviderDriver>(`${this.resourceUrl}${id}/drivers/`, providerDriver).pipe(
      map(response => Deserialize(response, ProviderDriver))
    );
  }

  /**
   * @param id
   * @param providerDispatcher
   */
  saveProviderDispatcher(id: number, providerDispatcher: ProviderDispatcher): Observable<ProviderDispatcher> {
    return this.post<ProviderDispatcher>(`${this.resourceUrl}${id}/dispatchers/`, providerDispatcher).pipe(
      map(response => Deserialize(response, ProviderDriver))
    );
  }

  /**
   * @param providerId
   * @param ignoredRideRequest
   * @permission ride_request_update
   */
  ignoreRideRequest(providerId: number, ignoredRideRequest: IgnoredRideRequest): Observable<IgnoredRideRequest> {
    return this.post<IgnoredRideRequest>(
      `${this.resourceUrl}${providerId}/rideRequests/${ignoredRideRequest.rideRequestId}/ignoredRideRequests/`,
      Serialize(ignoredRideRequest)
    ).pipe(map(response => Deserialize(response, IgnoredRideRequest)));
  }

  /**
   * @param providerId
   * @param rideId
   * @param interestedDriver
   * @permission provider_create
   */
  addInterestedDriverForRideRequest(providerId: number, rideId: number, interestedDriver: InterestedDriver): Observable<InterestedDriver> {
    return this.post<InterestedDriver>(
      `${this.resourceUrl}${providerId}/rideRequests/${rideId}/interestedDrivers`,
      Serialize(interestedDriver, InterestedDriver)
    ).pipe(map(response => Deserialize(response, InterestedDriver)));
  }

  /**
   * @permission ride_request_update
   */
  deleteInterestedProviderDriverForRideRequest(providerId: number, rideRequestId: number, interestedDriverId: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${providerId}/rideRequests/${rideRequestId}/interestedDrivers/${interestedDriverId}`);
  }

  /**
   * @permission ride_request_update
   */
  cancelReservation(providerId: number, rideRequestId: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${providerId}/rideRequests/${rideRequestId}`);
  }

  /**
   * @param providerId
   * @param reservationId
   * @param reservation
   * @permission reservation_update
   */
  patchReservation(providerId: number, reservationId: number, reservation: Reservation): Observable<ProviderDispatchFeedItem> {
    return this.patch<ProviderDispatchFeedItem>(
      `${this.resourceUrl}${providerId}/reservations/${reservationId}`,
      Serialize(reservation, Reservation)
    ).pipe(map(response => Deserialize(response, ProviderDispatchFeedItem)));
  }

  /**
   * @param id
   * @param provider
   * @permission provider_management
   */
  patchProvider(id: number, provider: Provider): Observable<Provider> {
    return this.patch<Provider>(`${this.resourceUrl}${id}`, provider).pipe(map(response => Deserialize(response, Provider)));
  }

  /**
   * @param id
   * @param providerDriver
   * @permission driver_management
   */
  patchProviderDriver(id: number, providerDriver: ProviderDriver): Observable<ProviderDriver> {
    return this.patch<ProviderDriver>(`${this.resourceUrl}${id}/drivers/${providerDriver.id}`, providerDriver).pipe(
      map(response => Deserialize(response, ProviderDriver))
    );
  }

  /**
   * @param id
   * @permission provider_management
   */
  deleteProvider(id: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${id}`);
  }

  /**
   * @param id
   * @param driverId
   * @permission driver_management
   */
  deleteProviderDriver(id: number, driverId: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${id}/drivers/${driverId}`);
  }

  /**
   * @param id
   * @param dispatcherId
   * @permission provider_management
   */
  deleteProviderDispatcher(id: number, dispatcherId: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${id}/dispatchers/${dispatcherId}`);
  }

  /**
   * @param providerId
   * @param rideRequestId
   * @permission ride_request_update
   */
  removeIgnoredRideRequest(providerId: number, rideRequestId: number): Observable<boolean> {
    return this.delete(`${this.resourceUrl}${providerId}/rideRequests/${rideRequestId}/ignoredRideRequests/`);
  }
}
